import { Link } from "react-router-dom";
import { Phone, MessageCircle, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { STORE, waLink, telLink } from "@/lib/store";

const HERO_IMG =
    "https://images.unsplash.com/photo-1635335874521-7987db781153?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxlbGVjdHJpY2FsJTIwd2lyZXMlMjBpbmR1c3RyaWFsfGVufDB8fHx8MTc4MjExMTE1NHww&ixlib=rb-4.1.0&q=85";

export default function Hero() {
    return (
        <section
            data-testid="hero-section"
            className="relative overflow-hidden bg-charcoal text-white pt-16 pb-24 md:pt-24 md:pb-32"
        >
            <div className="absolute inset-0 bg-blueprint opacity-70" />
            <div className="absolute inset-0 bg-gradient-to-b from-charcoal via-charcoal/95 to-charcoal-900 pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    <div className="lg:col-span-7 animate-fade-up">
                        <h1 className="font-display font-extrabold tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
                            Electrical &
                            <br />
                            <span className="text-safety">plumbing</span> supplies.
                        </h1>

                        <p className="mt-6 max-w-xl text-white/70 text-base sm:text-lg leading-relaxed">
                            {STORE.tagline} Copper wire, switches, MCBs, PVC pipes, taps,
                            valves and fittings — WhatsApp us for a quote and delivery in {STORE.serviceArea}.
                        </p>

                        <div className="mt-8 flex flex-wrap items-center gap-3">
                            <a href={telLink()} data-testid="hero-call-btn">
                                <Button className="h-12 px-6 rounded-full bg-white text-charcoal hover:bg-white/90 font-semibold">
                                    <Phone className="w-4 h-4 mr-2" /> Call {STORE.phoneDisplay}
                                </Button>
                            </a>
                            <a href={waLink()} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-btn">
                                <Button className="h-12 px-6 rounded-full bg-safety hover:bg-safety-hover text-white font-semibold btn-shine">
                                    <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp us
                                </Button>
                            </a>
                            <Link to="/contact" data-testid="hero-contact-link">
                                <Button
                                    variant="outline"
                                    className="h-12 px-6 rounded-full border-white/20 text-white hover:bg-white/10 font-semibold"
                                >
                                    Contact form
                                </Button>
                            </Link>
                        </div>

                        <div className="mt-10 grid grid-cols-3 gap-3 max-w-lg">
                            {[
                                { k: "Wire & MCBs", v: "Havells, Finolex, Anchor" },
                                { k: "Pipes & taps", v: "PVC, CPVC & fittings" },
                                { k: "Pan-India", v: "delivery available" },
                            ].map((t) => (
                                <div key={t.v} className="border border-white/10 rounded-xl px-4 py-3 bg-white/[0.02]">
                                    <div className="font-display font-extrabold text-lg sm:text-xl leading-tight">{t.k}</div>
                                    <div className="text-xs text-white/60 mt-1 leading-tight">{t.v}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-5 relative">
                        <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-black/40 aspect-[4/5]">
                            <img
                                src={HERO_IMG}
                                alt="Electrical wires and supplies"
                                className="w-full h-full object-cover"
                                loading="eager"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-charcoal-900 via-charcoal-900/40 to-transparent" />

                            <div className="absolute top-5 left-5 inline-flex items-center gap-2 bg-safety text-white text-xs font-bold uppercase tracking-widest px-3 py-2 rounded-full">
                                <ShieldCheck className="w-3.5 h-3.5" /> Trusted since {STORE.since}
                            </div>

                            <div className="absolute bottom-5 left-5 right-5 rounded-2xl bg-white/95 backdrop-blur p-4 text-charcoal">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-safety">Specialists in</p>
                                        <p className="font-display font-bold text-base mt-0.5">Electrical & plumbing</p>
                                    </div>
                                    <a
                                        href={waLink()}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid="hero-whatsapp-link"
                                        className="text-xs font-semibold underline underline-offset-4 hover:text-safety"
                                    >
                                        Get a quote
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div className="hidden lg:block absolute -bottom-8 -left-8 rounded-2xl bg-white text-charcoal p-4 w-56 border border-black/5 shadow-2xl">
                            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-safety">Owner's word</p>
                            <p className="mt-1 font-display font-bold leading-tight text-sm">
                                "Right gauge, right fitting — every time."
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
